/**
 * Timeline date range utilities
 * Handles the 15-month date range logic based on current month
 */

export interface TimelineMonthInfo {
  title: string;
  year: number;
  monthIndex: number;
}

export interface TimelineDateRange {
  startDate: string;
  endDate: string;
  firstYear: number;
  secondYear: number;
  secondYearPosition: number;
  monthsWithYears: TimelineMonthInfo[];
}

/**
 * Calculate timeline date range based on current date
 * 
 * Logic:
 * - Jan-Sep: Show Oct (prev year) -> Dec (current year) = 15 months
 * - Oct-Dec: Show Jan (current year) -> Mar (next year) = 15 months
 */
export function getTimelineDateRange(): TimelineDateRange {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, so add 1
  
  const allMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let monthsWithYears: TimelineMonthInfo[] = [];
  let startDate: string;
  let endDate: string;
  let firstYear: number;
  let secondYear: number;
  let secondYearPosition: number;
  
  // January to September: get previous year Oct to current year Dec
  if (currentMonth >= 1 && currentMonth <= 9) {
    startDate = `${currentYear - 1}-10-01`;
    endDate = `${currentYear}-12-31`;
    firstYear = currentYear - 1;
    secondYear = currentYear;
    secondYearPosition = 3; // After Oct, Nov, Dec (indices 0, 1, 2)
    
    // Previous year: Oct, Nov, Dec
    for (let i = 9; i < 12; i++) {
      monthsWithYears.push({
        title: allMonths[i],
        year: currentYear - 1,
        monthIndex: i + 1
      });
    }
    // Current year: Jan → Dec
    for (let i = 0; i < 12; i++) {
      monthsWithYears.push({
        title: allMonths[i],
        year: currentYear,
        monthIndex: i + 1
      });
    }
  } 
  // October to December: get current year Jan to next year Mar
  else {
    startDate = `${currentYear}-01-01`;
    endDate = `${currentYear + 1}-03-31`;
    firstYear = currentYear;
    secondYear = currentYear + 1;
    secondYearPosition = 12; // After all 12 months of current year
    
    // Current year: Jan → Dec
    for (let i = 0; i < 12; i++) {
      monthsWithYears.push({
        title: allMonths[i],
        year: currentYear,
        monthIndex: i + 1
      });
    }
    // Next year: Jan, Feb, Mar
    for (let i = 0; i < 3; i++) {
      monthsWithYears.push({
        title: allMonths[i],
        year: currentYear + 1,
        monthIndex: i + 1
      });
    }
  }
  
  return {
    startDate,
    endDate,
    firstYear,
    secondYear,
    secondYearPosition,
    monthsWithYears
  };
}

/**
 * Calculate target year and month for a given month index in the timeline
 * @param monthIndex - Index of the month in the timeline (0-14)
 * @returns Object with targetYear and targetMonth
 */
export function getTargetYearMonth(monthIndex: number): { targetYear: number; targetMonth: number } {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  let targetYear: number;
  let targetMonth: number;
  
  // Determine the year based on the month position in the timeline
  if (currentMonth >= 1 && currentMonth <= 9) {
    // Jan-Sep: Oct(prev year) -> Dec(current year)
    if (monthIndex < 3) {
      // Oct, Nov, Dec of previous year
      targetYear = currentYear - 1;
      targetMonth = 10 + monthIndex; // Oct=10, Nov=11, Dec=12
    } else {
      // Jan-Dec of current year
      targetYear = currentYear;
      targetMonth = monthIndex - 2; // Jan=1 (index 3 - 2 = 1), Feb=2, ..., Dec=12
    }
  } else {
    // Oct-Dec: Jan(current year) -> Mar(next year)
    if (monthIndex < 12) {
      // Jan-Dec of current year
      targetYear = currentYear;
      targetMonth = monthIndex + 1; // Jan=1, Feb=2, ..., Dec=12
    } else {
      // Jan-Mar of next year
      targetYear = currentYear + 1;
      targetMonth = monthIndex - 11; // Jan=1 (index 12 - 11 = 1), Feb=2, Mar=3
    }
  }
  
  return { targetYear, targetMonth };
}

